<template>
  <el-dialog
    :visible="visible"
    :show-close="false"
    @close="abortCreate"
  >
    <ContentHeader
      slot="title"
      :title="
        isModalEdit
          ? $t('quarantine.update.updateSample')
          : $t('quarantine.create.createSample')
      "
    />
    <ValidationObserver v-slot="{ handleSubmit, invalid }">
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row :gutter="20">
          <el-col :span="8">
            <FormItem :label="$t('quarantine.create.productName')">
              <el-input v-model="formData.productName" />
            </FormItem>
          </el-col>
          <el-col :span="8">
            <FormItem :label="$t('quarantine.create.customerProductName')">
              <el-input v-model="formData.customerProductName" />
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('quarantine.create.lot')">
              <el-input v-model="formData.lot" />
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('quarantine.create.date')">
              <el-date-picker
                v-model="formData.date"
                type="date"
              />
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('quarantine.create.sector')">
              <el-select
                v-model="formData.sector"
                placeholder=""
                clearable
              >
                <el-option
                  v-for="option in sectorOptions"
                  :key="option.value"
                  :label="$t(option.label)"
                  :value="option.value"
                />
              </el-select>
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem
              rules="numeric"
              :label="$t('quarantine.create.packagesNumber')"
            >
              <el-input
                v-model.number="formData.packagesNumber"
                type="number"
              />
            </FormItem>
          </el-col>
          <el-col :span="24">
            <FormItem :label="$t('quarantine.create.notes')">
              <el-input
                v-model="formData.notes"
                type="textarea"
              />
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('quarantine.create.finalCustomer')">
              <el-autocomplete
                v-model="formData.finalCustomer"
                style="width: 100%"
                :fetch-suggestions="finalCustomerList"
                value-key="label"
                placeholder=""
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('quarantine.create.sampleDescription')">
              <el-select
                v-model="formData.sampleDescription"
                filterable
                placeholder=""
                clearable
              >
                <el-option
                  v-for="option in sampleDescriptionOptions"
                  :key="option.value"
                  :label="$t(option.label)"
                  :value="option.value"
                />
              </el-select>
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem :label="$t('quarantine.create.costCenter')">
              <el-input
                v-model="formData.costCenter"
                :maxlength="100"
              />
            </FormItem>
          </el-col>
          <el-col :span="6">
            <FormItem
              rules="required"
              :label="$t('quarantine.create.quarantineReason')"
            >
              <el-select
                v-model="formData.reason"
                placeholder=""
                clearable
              >
                <el-option
                  v-for="reason in QUARANTINE_REASONS"
                  :key="reason"
                  :label="
                    $t(
                      `enums.quarantineReasons.${QUARANTINE_REASONS_DESC[reason]}`
                    )
                  "
                  :value="reason"
                />
              </el-select>
            </FormItem>
          </el-col>
          <el-col :span="24">
            <FormItem :label="$t('quarantine.create.comments')">
              <el-input
                v-model="formData.comments"
                type="textarea"
              />
            </FormItem>
          </el-col>
        </el-row>
        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortCreate"
          >
            {{ $t('quarantine.create.abortCreate') }}
          </el-button>
          <el-button
            v-if="isModalEdit"
            type="primary"
            native-type="submit"
            :disabled="invalid || hasNoValues"
            @click.prevent="handleSubmit(updateSample)"
          >
            {{ $t('quarantine.update.updateSample') }}
          </el-button>
          <el-button
            v-else
            type="primary"
            native-type="submit"
            :disabled="invalid || hasNoValues"
            @click.prevent="handleSubmit(createSample)"
          >
            {{ $t('quarantine.create.createSample') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import { quarantineApi, customersApi } from '@/api';
  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import AutocompleteItem from '@/components/AutocompleteItem.vue';
  import {
    sectorsList,
    sampleDescriptionsList,
    QUARANTINE_REASONS,
    QUARANTINE_REASONS_DESC,
  } from '@/config/constants';
  import { enumOptions } from '@/config/enumOptions';

  const defaultData = {
    labSampleCode: null,
    productName: null,
    customerProductName: null,
    lot: null,
    date: null,
    sector: null,
    packagesNumber: null,
    notes: null,
    finalCustomer: null,
    sampleDescription: null,
    comments: null,
    costCenter: null,
    reason: null,
  };

  export default {
    name: 'CreateEditSample',
    components: {
      FormItem,
      ContentHeader,
      FormButtons,
      AutocompleteItem,
    },
    props: {
      visible: { type: Boolean, default: false },
      isModalEdit: { type: Boolean, default: false },
      initialData: { type: Object, default: () => defaultData },
    },
    computed: {
      sectorOptions() {
        return enumOptions(
          sectorsList,
          'samples.sectors',
          this.formData.sector
        );
      },
      sampleDescriptionOptions() {
        return enumOptions(
          sampleDescriptionsList,
          'samples.sampleDescriptions',
          this.formData.sampleDescription
        );
      },
      hasNoValues() {
        return Object.values(this.formData).every(
          (val) => val === null || val === ''
        );
      },
    },
    data() {
      return {
        formData: { ...defaultData },
        sectorsList,
        sampleDescriptionsList,
        QUARANTINE_REASONS,
        QUARANTINE_REASONS_DESC,
      };
    },
    watch: {
      initialData(data) {
        if (Object.keys(data).length > 0) {
          this.formData = { ...data };
        }
      },
    },
    methods: {
      buildFormToSend() {
        return {
          // all fields are optional
          ...(this.formData.productName !== null && {
            productName: this.formData.productName,
          }),
          ...(this.formData.packagesNumber !== null && {
            packagesNumber: this.formData.packagesNumber,
          }),
          ...(this.formData.sampleDescription !== null && {
            sampleDescription: this.formData.sampleDescription,
          }),
          ...(this.formData.date !== null && {
            date: this.formData.date,
          }),
          ...(this.formData.labSampleCode !== null && {
            labSampleCode: this.formData.labSampleCode,
          }),
          ...(this.formData.customerProductName !== null && {
            customerProductName: this.formData.customerProductName,
          }),
          ...(this.formData.lot !== null && {
            lot: this.formData.lot,
          }),
          ...(this.formData.sector !== null && {
            sector: this.formData.sector,
          }),
          ...(this.formData.notes !== null && {
            notes: this.formData.notes,
          }),
          ...(this.formData.finalCustomer !== null && {
            finalCustomer: this.formData.finalCustomer,
          }),
          ...(this.formData.reason !== null && {
            reason: this.formData.reason,
          }),
          ...(this.formData.comments !== null && {
            comments: this.formData.comments,
          }),
          ...(this.formData.costCenter !== null && {
            costCenter: this.formData.costCenter,
          }),
        };
      },
      async createSample() {
        try {
          await quarantineApi.create(null, this.buildFormToSend());
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('quarantine.create.success'),
          });
          this.$emit('dialogClose', { success: true });
          this.formData = { ...defaultData };
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async updateSample() {
        try {
          await quarantineApi.patch(
            this.$route.params.sampleId,
            this.buildFormToSend()
          );
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('quarantine.update.success'),
          });
          this.$emit('dialogClose', { success: true });
          this.formData = { ...defaultData };
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async finalCustomerList(value, cb) {
        if (value && value.length >= 3) {
          const response = await customersApi.autocompleteFetch(
            'name',
            value,
            false
          );
          const customers = response.data.map((c) => ({
            value: c.name,
            label: c.name,
          }));
          cb(customers);
        } else {
          cb([]);
        }
      },
      abortCreate() {
        this.formData = { ...defaultData };
        this.$emit('dialogClose', { success: false });
      },
    },
  };
</script>
